import { EPlatform } from '@enums/platform.enum';
import backendHttpClient from '@services/_httpClient/backend.http-client';

import type { GetAllProvidersParams } from './dtos/getAll.dto';
import type {
  UpdateProviderDto,
  UpdateProviderPayload,
} from './dtos/update.dto';
import type { ProviderRepository } from './repository';
import type {
  GetAllProvidersOutput,
  GetAllProvidersResponse,
} from './types/getAll.type';
import type {
  GetAllProviderNotesOutput,
  GetAllProviderNotesParams,
  GetAllProviderNotesQueryParams,
  GetAllProviderNotesResponse,
} from './types/getAllNotes.type';
import type {
  GetAllProvidersToSelectOutput,
  GetAllProvidersToSelectResponse,
} from './types/getAllToSelect.type';
import type {
  GetProviderByIdOutput,
  GetProviderByIdResponse,
} from './types/getById.type';
import type {
  UpdateProviderOutput,
  UpdateProviderResponse,
} from './types/update.type';

class ProviderService implements ProviderRepository {
  public async sendInvite(id: string): Promise<void> {
    await backendHttpClient.post<void>({
      path: `providers/${id}/invite`,
    });
  }

  public async getAll(
    params: GetAllProvidersParams,
  ): Promise<GetAllProvidersOutput> {
    const { data } = await backendHttpClient.get<
      GetAllProvidersResponse,
      GetAllProvidersParams
    >({
      path: 'providers',
      params,
    });

    return { list: data.list, pagination: data.pagination, count: data.count };
  }

  public async getById(id: string): Promise<GetProviderByIdOutput> {
    const { data } = await backendHttpClient.get<GetProviderByIdResponse>({
      path: `providers/${id}`,
    });

    return { provider: data.provider };
  }

  public async getAllToSelect(): Promise<GetAllProvidersToSelectOutput> {
    const { data } =
      await backendHttpClient.get<GetAllProvidersToSelectResponse>({
        path: 'providers/select',
      });

    const output: GetAllProvidersToSelectOutput = {
      list: data.list.map(provider => ({
        label: provider.companyName,
        value: provider.id,
      })),
    };

    return output;
  }

  public async update({
    id,
    ...dto
  }: UpdateProviderPayload): Promise<UpdateProviderOutput> {
    const { data } = await backendHttpClient.put<
      UpdateProviderResponse,
      UpdateProviderDto
    >({
      path: `providers/${id}`,
      body: dto,
    });

    return { provider: data.provider };
  }

  public async getAllNotes(
    params: GetAllProviderNotesParams,
  ): Promise<GetAllProviderNotesOutput> {
    const { data } = await backendHttpClient.get<
      GetAllProviderNotesResponse,
      GetAllProviderNotesQueryParams
    >({
      path: `providers/${params.id}/notes`,
      params: {
        page: params.page,
        perPage: params.perPage,
        platform: EPlatform.Connect,
      },
    });

    return {
      list: data.list,
      pagination: data.pagination,
    };
  }
}

export const providerService = new ProviderService();
